/**
 * useAccounts – multi-account household store (Zustand).
 */
import { create } from 'zustand'
import {
  getAccounts,
  createAccount,
  updateAccount as apiUpdateAccount,
  deleteAccount as apiDeleteAccount,
  getHoldings,
  addHolding as apiAddHolding,
  updateHolding as apiUpdateHolding,
  deleteHolding as apiDeleteHolding,
  importCSV as apiImportCSV,
  getHouseholdSummary,
} from '@/lib/api'
import type { Account, Holding, HouseholdSummary, CSVImportResponse, ReconciliationFlag } from '@/lib/types'

interface AccountsState {
  accounts: Account[]
  holdings: Record<number, Holding[]>  // keyed by account_id
  summary: HouseholdSummary | null
  loading: boolean
  error: string | null

  fetchAccounts: () => Promise<void>
  createAccount: (body: {
    name: string; provider: string; account_type: string;
    currency?: string; owner?: string; notes?: string; discretionary?: boolean;
  }) => Promise<Account | null>
  updateAccount: (id: number, body: Record<string, unknown>) => Promise<Account | null>
  deleteAccount: (id: number) => Promise<void>
  fetchHoldings: (accountId: number) => Promise<void>
  /** Returns true when the holding was created, false when the API call
   * failed — callers must keep their modal open and show the error. */
  addHolding: (accountId: number, body: {
    name: string; quantity: number; value: number;
    [k: string]: unknown;
  }) => Promise<boolean>
  /** Returns any advisory data-integrity flags the backend raised for the
   * edit (e.g. a pence/pound 100x slip). Empty array when none.
   * Returns null when the save FAILED — callers must not exit edit mode. */
  updateHolding: (accountId: number, holdingId: number, body: Record<string, unknown>) => Promise<ReconciliationFlag[] | null>
  deleteHolding: (accountId: number, holdingId: number) => Promise<void>
  importCSV: (
    accountId: number,
    file: File,
    opts?: { dryRun?: boolean; merge?: boolean; autoResolveIsins?: boolean },
  ) => Promise<CSVImportResponse | null>
  fetchSummary: () => Promise<void>
  clearError: () => void
}

export const useAccounts = create<AccountsState>()((set, get) => ({
  accounts: [],
  holdings: {},
  summary: null,
  loading: false,
  error: null,

  fetchAccounts: async () => {
    set({ loading: true, error: null })
    try {
      const accounts = await getAccounts()
      set({ accounts, loading: false })
    } catch (e) {
      set({ error: String(e), loading: false })
    }
  },

  createAccount: async (body) => {
    try {
      const account = await createAccount(body)
      set(s => ({ accounts: [...s.accounts, account] }))
      return account
    } catch (e) {
      set({ error: String(e) })
      return null
    }
  },

  updateAccount: async (id, body) => {
    try {
      const account = await apiUpdateAccount(id, body)
      set(s => ({ accounts: s.accounts.map(a => a.id === id ? account : a) }))
      return account
    } catch (e) {
      set({ error: String(e) })
      return null
    }
  },

  deleteAccount: async (id) => {
    try {
      await apiDeleteAccount(id)
      set(s => ({ accounts: s.accounts.filter(a => a.id !== id) }))
    } catch (e) {
      set({ error: String(e) })
    }
  },

  fetchHoldings: async (accountId) => {
    try {
      const list = await getHoldings(accountId)
      set(s => ({ holdings: { ...s.holdings, [accountId]: list } }))
    } catch (e) {
      set({ error: String(e) })
    }
  },

  addHolding: async (accountId, body) => {
    try {
      await apiAddHolding(accountId, body)
      await get().fetchHoldings(accountId)
      await get().fetchAccounts()
      await get().fetchSummary()
      return true
    } catch (e) {
      set({ error: String(e) })
      return false
    }
  },

  updateHolding: async (accountId, holdingId, body) => {
    try {
      const updated = await apiUpdateHolding(accountId, holdingId, body)
      set(s => ({
        holdings: {
          ...s.holdings,
          [accountId]: (s.holdings[accountId] ?? []).map(h => h.id === holdingId ? updated : h),
        },
      }))
      await get().fetchAccounts()
      await get().fetchSummary()
      return updated?.reconciliation_flags ?? []
    } catch (e) {
      // null (not []) so callers can tell "saved, no flags" from "save failed"
      set({ error: String(e) })
      return null
    }
  },

  deleteHolding: async (accountId, holdingId) => {
    try {
      await apiDeleteHolding(accountId, holdingId)
      set(s => ({
        holdings: {
          ...s.holdings,
          [accountId]: (s.holdings[accountId] ?? []).filter(h => h.id !== holdingId),
        },
      }))
      await get().fetchAccounts()
      await get().fetchSummary()
    } catch (e) {
      set({ error: String(e) })
    }
  },

  importCSV: async (accountId, file, opts) => {
    try {
      const result = await apiImportCSV(accountId, file, opts)
      // Skip refetch on dry-run — we didn't write anything.
      if (!opts?.dryRun) {
        await get().fetchHoldings(accountId)
        await get().fetchAccounts()
        await get().fetchSummary()
      }
      return result
    } catch (e) {
      set({ error: String(e) })
      return null
    }
  },

  fetchSummary: async () => {
    try {
      const summary = await getHouseholdSummary()
      set({ summary })
    } catch (e) {
      set({ error: String(e) })
    }
  },

  clearError: () => set({ error: null }),
}))
